import moment, { Moment } from "moment-timezone";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Hash } from "../../../api/@types/apiTypes";
import { ApiResponse } from "../../../api/apiResponse";
import {
  showAvailableSlots,
  showAvailableTutors,
  updateReadingScheduleSlot,
} from "../../../api/endpoints/candidate/readingScheduleSlots";
import TutorModel from "../../../api/models/TutorModel";
import ListeningSessions from "../../../components/candidate-sub-components/ListeningSessions";
import SelfLearningPlatform from "../../../components/candidate-sub-components/SelfLearningPlatform";
import BookSessionModal from "../../../components/modal/views/BookSessionModal";
import { makeApiRequest } from "../../../helpers";
import ApiDateParser from "../../../helpers/parsers/apiDateParser";
import { getHoursList } from "../../../services/schdeuleTimeOptionsService";

const DATE_FORMAT = "D/M/YYYY";
const HOURS_STEP = 20; // 20 minutes
export default function CandidateHome() {
  const LocalBasePath = "page.candidate.dashboard.homepage";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [isOpenBookSession, setIsOpenBookSession] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [selectedTime, setSelectedTime] = useState<Moment | null>(null);
  const [tutorsList, setTutorsList] = useState<TutorModel[]>([]);
  const [selectedTutor, setSelectedTutor] = useState<TutorModel | null>(null);
  const [availableSlots, setAvailableSlots] = useState<Hash<Moment[]>>({});

  const [sessionsComponentKey, setSessionsComponentKey] = useState(
    moment().unix()
  );
  const refetchSessions = () => {
    setSessionsComponentKey(moment().unix());
  };

  const fetchAvailableSlots = async () => {
    const startDate = moment().format(DATE_FORMAT);
    const endDate = moment().add(7, "days").format(DATE_FORMAT);
    await makeApiRequest(
      () => showAvailableSlots(startDate, endDate),
      (response: ApiResponse) => {
        // filter out empty days
        const slotEntries: [key: string, value: Moment[]][] = Object.entries(
          response.data as Hash<number[]>
        )
          .filter(([key, value]) => value.length > 0)
          .map(([key, value]) => [
            key,
            value.map((time) => ApiDateParser.parseTimestamp(time)),
          ]);

        const slots = Object.fromEntries(slotEntries);

        //filter out times before now
        const today = moment().format("YYYY-MM-DD");
        if (today in slots) {
          slots[today] = slots[today].filter((time) => time.isAfter(moment()));
        }

        setAvailableSlots(slots);
        const firstAvailableDate = Object.keys(slots)[0];
        setSelectedDate(moment(firstAvailableDate, "YYYY-MM-DD"));
      }
    );
  };

  const fetchAvailableTutors = async () => {
    const date = selectedDate?.format(DATE_FORMAT);
    const startTime = selectedTime?.unix();
    await makeApiRequest(
      () => showAvailableTutors(date!, startTime!),
      (response: ApiResponse) => {
        const tutors = TutorModel.fromApiResponse(response.data);
        setTutorsList(tutors);
      }
    );
  };

  const handleBookSession = async () => {
    const date = selectedDate?.format(DATE_FORMAT);
    const start_time = ApiDateParser.toNormalizedApiDate(selectedTime!);
    const end_time = ApiDateParser.toNormalizedApiDate(selectedTime!.add(HOURS_STEP, "minutes"));
    const tutor_id = selectedTutor?.id;
    await makeApiRequest(
      () =>
        updateReadingScheduleSlot({
          schedule_slots_attributes: [
            {
              date,
              start_time,
              end_time,
              tutor_id,
            },
          ],
        }),
      (response: ApiResponse) => {
        handleCloseBookSession();
        // Force refetch sessions
        refetchSessions();
      }
    );
  };

  useEffect(() => {
    if (selectedDate && selectedTime) {
      fetchAvailableTutors();
    }
  }, [selectedDate, selectedTime]);

  const handleOpenBookSession = async () => {
    await fetchAvailableSlots();
    setIsOpenBookSession(true);
  };

  const handleCloseBookSession = () => {
    setIsOpenBookSession(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedTutor(null);
    setTutorsList([]);
  };

  const handleSelectDate = (date: Moment | null) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setSelectedTutor(null);
    setTutorsList([]);
  };

  const handleSelectTime = (time: Moment | null) => {
    setSelectedTime(time);
    setSelectedTutor(null);
    setTutorsList([]);
  };

  const handleSelectTutor = (tutor: TutorModel | null) => {
    setSelectedTutor(tutor);
  };

  // Generating week days starting from Today
  const startOfTheWeek = moment();
  const dates = [...Array(7)].map((_, i) => {
    const date = startOfTheWeek.clone().add(i, "days");
    return {
      date: startOfTheWeek.clone().add(i, "day"),
      isDisabled: !availableSlots[date.format("YYYY-MM-DD")]?.length,
    };
  });

  const hourMapper = (hour: Moment) => {
    // Disable hour that is not available
    const isDisabled = !availableSlots[
      selectedDate?.format("YYYY-MM-DD") || ""
    ]?.find((time) => {
      const timeString = time.format("HH:mm");
      return timeString === hour?.format("HH:mm");
    });

    return {
      dateTime: hour,
      isDisabled,
    };
  };

  //Generate am/pm time slots
  const amStart = moment("00:00", "HH:mm");
  const numberOfSlots = (24 * 60) / HOURS_STEP;
  const amHoursList = getHoursList(amStart, numberOfSlots / 2, HOURS_STEP).map(
    hourMapper
  );

  const pmStart = moment("12:00", "HH:mm");
  const pmHoursList = getHoursList(pmStart, numberOfSlots / 2, HOURS_STEP).map(
    hourMapper
  );

  return (
    <div className="w-full h-fit min-h-full flex flex-col bg-gray-50">
      <div className="py-6  md:px-36 flex flex-col gap-6 ">
        <div className="w-full">
          <div className="flex flex-col gap-6  px-4 md:px-0  ">
            <h2 className="font-medium text-xl leading-8  text-gray-900">
              {t("quranMaher")}
            </h2>
            <div className="border-b border-gray-200 pb-6 ">
              <ListeningSessions
                openBookSession={handleOpenBookSession}
                key={sessionsComponentKey}
              />
            </div>
            <div className="w-full">
              <SelfLearningPlatform />
            </div>
          </div>
        </div>
      </div>
      <BookSessionModal
        isOpen={isOpenBookSession}
        onClose={handleCloseBookSession}
        onSelectDate={handleSelectDate}
        onSelectTime={handleSelectTime}
        tutorsList={tutorsList}
        selectedTutor={selectedTutor}
        onSelectTutor={handleSelectTutor}
        onBookSession={handleBookSession}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        amHoursList={amHoursList}
        pmHoursList={pmHoursList}
        dates={dates}
      />
    </div>
  );
}
